"use client";

import { useEffect, type CSSProperties } from "react";
import { useStore } from "../store";

const TOAST_DURATION = 3500;

/** Renders a stack of auto-dismissing toast notifications. */
export function ToastContainer() {
  const toasts = useStore((s) => s.toasts);
  const removeToast = useStore((s) => s.removeToast);

  if (toasts.length === 0) return null;

  return (
    <div style={containerStyle}>
      {toasts.map((toast) => (
        <ToastItem
          key={toast.id}
          id={toast.id}
          message={toast.message}
          type={toast.type}
          onDismiss={removeToast}
        />
      ))}
    </div>
  );
}

function ToastItem({
  id,
  message,
  type,
  onDismiss,
}: {
  id: string;
  message: string;
  type: "error" | "success" | "info";
  onDismiss: (id: string) => void;
}) {
  useEffect(() => {
    const timer = setTimeout(() => onDismiss(id), TOAST_DURATION);
    return () => clearTimeout(timer);
  }, [id, onDismiss]);

  return (
    <div
      style={{
        ...toastStyle,
        borderLeft: `3px solid ${typeColors[type]}`,
        animation: "toast-slide-in 0.25s ease-out",
      }}
    >
      <span style={{ flex: 1, minWidth: 0 }}>{message}</span>
      <button
        onClick={() => onDismiss(id)}
        aria-label="Dismiss"
        style={dismissStyle}
      >
        &times;
      </button>
    </div>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const typeColors = {
  error: "#ef4444",
  success: "#22c55e",
  info: "#6cf",
};

const containerStyle: CSSProperties = {
  position: "fixed",
  bottom: 40,
  left: "50%",
  transform: "translateX(-50%)",
  zIndex: 3000,
  display: "flex",
  flexDirection: "column",
  gap: 8,
  maxWidth: 420,
  width: "calc(100% - 32px)",
  pointerEvents: "none",
};

const toastStyle: CSSProperties = {
  background: "var(--color-toast-bg)",
  color: "var(--color-toast-text)",
  borderRadius: 8,
  padding: "10px 14px",
  fontSize: "0.82rem",
  lineHeight: 1.4,
  display: "flex",
  alignItems: "center",
  gap: 10,
  boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
  pointerEvents: "auto",
};

const dismissStyle: CSSProperties = {
  background: "none",
  border: "none",
  color: "var(--color-text-faint)",
  fontSize: "1.1rem",
  cursor: "pointer",
  padding: "2px 4px",
  lineHeight: 1,
  flexShrink: 0,
};
